import { Calendar, Target, Clock, FileText, Edit3 } from 'lucide-preact'
import type { CampaignViewDto } from '../../types/campaign'

interface CampaignInfoSectionProps {
  campaign: CampaignViewDto
  onEditCampaign?: () => void
}

// Campaign status badge component
function CampaignStatusBadge({ status }: { status: string }) {
  const statusConfig = {
    Planned: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400', icon: Calendar },
    Active: { color: 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-400', icon: Target },
    Completed: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400', icon: Clock },
    Cancelled: { color: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400', icon: Calendar }
  }
  
  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.Planned
  const IconComponent = config.icon

  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
      <IconComponent className="w-4 h-4" />
      {status}
    </span>
  )
}

export function CampaignInfoSection({ campaign, onEditCampaign }: CampaignInfoSectionProps) {
  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not set'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const calculateDuration = () => {
    if (!campaign.StartDate || !campaign.EndDate) return 'Duration not set'
    
    const start = new Date(campaign.StartDate)
    const end = new Date(campaign.EndDate)
    const diffTime = Math.abs(end.getTime() - start.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays === 1) return '1 day'
    if (diffDays < 7) return `${diffDays} days`
    if (diffDays < 30) return `${Math.round(diffDays / 7)} weeks`
    return `${Math.round(diffDays / 30)} months`
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md border border-slate-200 dark:border-slate-700">
      {/* Header */}
      <div className="p-6 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
              <FileText className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
              Campaign Details
            </h2>
          </div>
          {onEditCampaign && (
            <button
              onClick={onEditCampaign}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              <Edit3 className="w-4 h-4" />
              Edit Campaign
            </button>
          )}
        </div>
        
        <div className="flex items-center gap-4">
          <CampaignStatusBadge status={campaign.Status} />
          <span className="text-sm text-slate-600 dark:text-slate-300">
            {calculateDuration()}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Basic Information */}
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                Campaign Name
              </h3>
              <p className="text-slate-900 dark:text-white font-medium">
                {campaign.Name}
              </p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                Target Audience
              </h3>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                {campaign.TargetAudience || 'Not specified'}
              </p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                Description
              </h3>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                {campaign.Description || 'No description provided'}
              </p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                Status
              </h3>
              <CampaignStatusBadge status={campaign.Status} />
            </div>
          </div>

          {/* Timeline Information */}
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                Start Date
              </h3>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span className="text-slate-900 dark:text-white">
                  {formatDate(campaign.StartDate)}
                </span>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                End Date
              </h3>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span className="text-slate-900 dark:text-white">
                  {formatDate(campaign.EndDate)}
                </span>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                Duration
              </h3>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-400" />
                <span className="text-slate-900 dark:text-white">
                  {calculateDuration()}
                </span>
              </div>
            </div>

            {/* Additional Information */}
            <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
              <h3 className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-3">
                Campaign IDs
              </h3>
              <div className="space-y-2 text-xs">
                <div>
                  <span className="text-slate-500 dark:text-slate-400">Campaign ID:</span>
                  <br />
                  <span className="font-mono text-slate-700 dark:text-slate-300">{campaign.Id}</span>
                </div>
                <div>
                  <span className="text-slate-500 dark:text-slate-400">Workspace ID:</span>
                  <br />
                  <span className="font-mono text-slate-700 dark:text-slate-300">{campaign.WorkspaceId}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
